import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppIndex, AppRow } from "./AppIndex";
import { AppMoney, AppMoneyAnswer, AppMoneyRow } from "./AppMoney";
import { AppMoneyCsv } from "./AppMoneyCsv";
import { AppMoneyContact, AppMoneyEdit, AppMoneyEntry, AppMoneyModel, AppMoneyNew } from "./AppMoneyModel";
import { AppMoneyScreen, AppMoneyView } from "./AppMoneyView";
import { AppMoneyTable, IAppMoneyAdder } from "./AppMoneyTable";
import { AppTaskAnswer, AppTaskApi } from "./AppTaskApi";

// AppMoneyRun - the browser half of the Revenue view: holds the filter for the visit, turns a press into ONE `/money`
// request through the host, and repaints from the answer.
// ⭐ THE TIE TO TASKS (see `MoneyOverlay`): a "Mark settled" on an item whose harvested block is also a task (`TaskId`)
// sends `/tasks done` for that task AFTER `/money` confirmed - two writers, one file each; reopening sends `reopen`.
// ⛔ NOTHING HERE SENDS A MESSAGE OR A REMINDER (decision 9) - `/money` and `/tasks` only change the owner's own notes.
// ⛔ One request in flight per item; a refusal is the service's sentence in ink, the row unchanged.

export type AppMoneyDone = (Status: number, Text: string) => void;

export interface IAppMoneyHost {
    MoneyPost(Body: string, Done: AppMoneyDone): void;
    TaskPost(Body: string, Done: AppMoneyDone): void;
    TaskPhone(): string;
    TaskKey(): string;
    MoneyIndex(): AppIndex;
    MoneyRepaint(): void;
    /** The browser's language tag, for the CSV separator. */
    MoneyCsvSep(): string;
    MoneyDownload(Name: string, Text: string): void;
}

export class AppMoneyRun implements IAppMoneyAdder {
    private Host: IAppMoneyHost;
    /** This paint's Contact choices for the add-row. */
    private Choices: AppMoneyContact[] = [];
    private Screen: AppMoneyScreen = new AppMoneyScreen();
    private Busy: Record<string, boolean> = {};

    public constructor(Host: IAppMoneyHost) {
        this.Host = Host;
    }

    /** "YYYY-MM-DD" in the browser's day. DOCUMENTED EXCEPTION: pure clock read. */
    public static Today = (): string => {
        const D: Date = new Date();
        return String(D.getFullYear()) + "-" + ("0" + String(D.getMonth() + 1)).slice(-2) + "-" + ("0" + String(D.getDate())).slice(-2);
    };

    /** The host, for `AppMoneyCall`. DOCUMENTED EXCEPTION: pure accessor. */
    public HostOf = (): IAppMoneyHost => {
        return this.Host;
    };

    /** The view's markup for this paint. */
    public Html = (CanChange: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            this.Screen.Today = AppMoneyRun.Today();
            this.Screen.All = AppMoneyModel.Entries(this.Host.MoneyIndex(), this.Screen.Today);
            this.Screen.CanChange = CanChange;
            return AppMoneyView.Html(this.Screen);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.Html");
            return "";
        }
    };

    /** Wire the painted view (one delegated listener per event kind on the root - it is replaced every paint). */
    public Wire = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Root: HTMLElement | null = document.getElementById(AppMoneyView.RootId);
            if (Root === null) { return; }
            Root.addEventListener("change", this.OnFilter);
            Root.addEventListener("input", this.OnSearch);
            Root.addEventListener("click", this.OnClick);
            this.MountTables();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.Wire");
        }
    };

    private OnFilter = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const El: HTMLInputElement = Ev.target as HTMLInputElement;
            const Name: string = nz(El.getAttribute(AppMoneyView.FilterAttr));
            if (Name.length === 0 || Name === "search") { return; }
            const F: Record<string, string> = this.Screen.Filter as unknown as Record<string, string>;
            const Key: string = Name.charAt(0).toUpperCase() + Name.substring(1);
            if (typeof F[Key] !== "string") { return; }
            F[Key] = nz(El.value);
            this.Host.MoneyRepaint();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.OnFilter");
        }
    };

    private OnSearch = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const El: HTMLInputElement = Ev.target as HTMLInputElement;
            if (El.getAttribute(AppMoneyView.FilterAttr) !== "search") { return; }
            this.Screen.Filter.Search = nz(El.value);
            this.Host.MoneyRepaint();
            const Again: HTMLInputElement | null = document.querySelector("[" + AppMoneyView.FilterAttr + "=search]");
            if (Again !== null) { Again.focus(); Again.setSelectionRange(Again.value.length, Again.value.length); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.OnSearch");
        }
    };

    private OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const El: Element | null = (Ev.target as Element).closest("button");
            if (El === null) { return; }
            if (El.getAttribute(AppMoneyView.ToolAttr) === "csv") { this.Csv(); }
            if (El.getAttribute(AppMoneyView.ToolAttr) === "excel") { AppMoneyTable.ExportAll("Revenue " + this.Screen.Today); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.OnClick");
        }
    };

    /** Each table host gets its shared `kEditableTable` (`AppMoneyTable.Mount`); the host marked `AddAttr` the add-row. */
    private MountTables = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Choices = AppMoneyModel.ContactChoices(this.Host.MoneyIndex());
            const Names: string[] = [];
            for (const C of this.Choices) { Names.push(C.Label); }
            const Shown: AppMoneyEntry[] = AppMoneyModel.Filtered(this.Screen.All, this.Screen.Filter);
            const Rows: Record<string, AppMoneyEntry[]> = { "new": [] };
            for (const G of AppMoneyModel.Groups(Shown, this.Screen.Filter)) { Rows[G.Key] = G.Rows; }
            for (const Host of Array.from(document.querySelectorAll<HTMLElement>("[" + AppMoneyView.GroupAttr + "]"))) {
                const Of: AppMoneyEntry[] | undefined = Rows[nz(Host.getAttribute(AppMoneyView.GroupAttr))];
                if (Of === undefined) { continue; }
                let Adder: IAppMoneyAdder | null = null;
                if (Host.getAttribute(AppMoneyView.AddAttr) === "1") { Adder = this; }
                AppMoneyTable.Mount(Host, Of, this.OnCellEdit, Adder, Names);
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.MountTables");
        }
    };

    /**
     * ⭐ THE ADD-ROW (`IAppMoneyAdder`): a complete row = ONE `/money add` under its page-made `v-` id (a second leave
     * while it is in flight sends nothing - `Busy`; after a refusal the same id is sent again, so it lands once).
     * Answers the columns still to fill (validation, shown in the row and said under the toolbar).
     */
    public AddRow = (Id: string, Cells: Record<string, string>): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const N: AppMoneyNew = AppMoneyModel.AddOf(Cells, this.Choices, AppMoneyRun.Today());
            if (N.Why.length > 0) { this.Say(N.Why); return N.Missing; }
            const E: AppMoneyEntry = new AppMoneyEntry();
            E.Id = Id;
            E.Href = N.Href;
            this.Send(E, AppMoney.Body(this.Host.TaskPhone(), this.Host.TaskKey(), N.Href, "add", Id, null, N.Add, ""), "");
            return [];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.AddRow");
            return [];
        }
    };

    /** A fresh `v-` id for a new row. */
    public NewId = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppMoney.UserPrefix + AppMoneyRun.Hex12();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.NewId");
            return "";
        }
    };

    /** ⭐ THE TABLE SEAM'S EDIT CALLBACK - the fallback editor and (after the swap) `kEditableTable` both call this. */
    public OnCellEdit = (Id: string, Column: string, Value: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const E: AppMoneyEntry | null = this.EntryOf(Id);
            if (E === null) { return; }
            const Edit: AppMoneyEdit = AppMoneyModel.EditOf(E, Column, Value);
            if (Edit.Why.length > 0) { this.Say(Edit.Why); return; }
            if (Edit.Action === "settle" || Edit.Action === "reopen") { this.Press(Id, Edit.Action); return; }
            this.Send(E, AppMoney.Body(this.Host.TaskPhone(), this.Host.TaskKey(), E.Href, "correct", Id, Edit.Set, null, ""), "");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.OnCellEdit");
        }
    };

    private Press = (Id: string, Act: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const E: AppMoneyEntry | null = this.EntryOf(Id);
            if (E === null) { return; }
            let Task: string = "";
            if (AppTaskApi.AiIdPattern.test(E.TaskId) === true) { Task = "done"; if (Act === "reopen") { Task = "reopen"; } }
            this.Send(E, AppMoney.Body(this.Host.TaskPhone(), this.Host.TaskKey(), E.Href, Act, Id, null, null, ""), Task);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.Press");
        }
    };

    /** One request per item; `Task` = the paired task's action after a confirmed answer ("" = none). */
    private Send = (E: AppMoneyEntry, Body: string, Task: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Body.length === 0) { this.Say(AppMoney.NotYetWhy()); return; }
            if (this.Busy[E.Id] === true) { return; }
            this.Busy[E.Id] = true;
            this.Say("Saving…");
            new AppMoneyCall(this, E, Task).Go(Body);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.Send");
        }
    };

    /** `AppMoneyCall`'s answer. */
    public Answered = (E: AppMoneyEntry, Task: string, Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            delete this.Busy[E.Id];
            const A: AppMoneyAnswer = AppMoney.ParseAnswer(Status, Text, "WACommander could not do that just now - this is at our end. Try again in a minute.");
            // REUSE-JUSTIFIED: A.Why is the service's refusal sentence (validation / restart window / a lost connection), a
            // designed fate-1 answer the owner can act on - routing it to KGlobalErrorHandler would latch every Revenue button.
            if (A.Ok === false) { this.Say(A.Why); return; }
            this.Adopt(E.Href, A.Row as AppMoneyRow);
            this.Screen.Note = "Saved.";
            if (Task.length > 0 && A.Changed === true) {
                this.Host.TaskPost(AppTaskApi.Body(this.Host.TaskPhone(), this.Host.TaskKey(), E.Href, Task, E.TaskId, "", null), this.OnTaskDone);
            }
            this.Host.MoneyRepaint();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.Answered");
        }
    };

    /** The paired task's answer: nothing to draw (the Tasks tab re-reads); a refusal is said. */
    private OnTaskDone = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const A: AppTaskAnswer = AppTaskApi.Parse(Status, Text, "");
            // REUSE-JUSTIFIED: the /tasks refusal sentence is a fate-1 answer (the money change already landed); a report would latch the page.
            if (A.Ok === false && A.Why.length > 0) { this.Say("Saved here; the matching task was not changed: " + A.Why); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.OnTaskDone");
        }
    };

    /** The answer's row into the page's index (replacing the same id, or added for a new hand item). */
    private Adopt = (Href: string, Row: AppMoneyRow): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Index: AppIndex = this.Host.MoneyIndex();
            for (const One of Index.Money) {
                if (One.MoneyRow !== null && (One.MoneyRow as AppMoneyRow).Id === Row.Id) { One.MoneyRow = Row; return; }
            }
            const Fresh: AppRow = new AppRow();
            Fresh.Href = Href;
            Fresh.Contact = this.ContactOf(Href);
            Fresh.Kind = "money";
            Fresh.MoneyRow = Row;
            Index.Money.push(Fresh);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.Adopt");
        }
    };

    /** 12 random hex from the browser's crypto - the page supplies the `v-` id so a retry lands once. */
    private static Hex12 = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const B: Uint8Array = new Uint8Array(6);
            crypto.getRandomValues(B);
            let Out: string = "";
            for (const One of Array.from(B)) { Out = Out + ("0" + One.toString(16)).slice(-2); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.Hex12");
            return "";
        }
    };

    private Csv = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Shown: AppMoneyEntry[] = AppMoneyModel.Filtered(this.Screen.All, this.Screen.Filter);
            let Zone: string = "UTC";
            try { Zone = nz(Intl.DateTimeFormat().resolvedOptions().timeZone); } catch (NoIntl) {
                // ERROR-SUPPRESSED-JUSTIFIED: no Intl zone - the header names UTC, which is then true of "today".
                Zone = "UTC";
            }
            this.Host.MoneyDownload(AppMoneyCsv.FileName(this.Screen.Today), AppMoneyCsv.Text(Shown, this.Host.MoneyCsvSep(), Zone));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.Csv");
        }
    };

    private EntryOf = (Id: string): AppMoneyEntry | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (const E of this.Screen.All) { if (E.Id === Id) { return E; } }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.EntryOf");
            return null;
        }
    };

    private ContactOf = (Href: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            for (const C of this.Host.MoneyIndex().Contacts) { if (C.Href === Href) { return C.Title; } }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.ContactOf");
            return "";
        }
    };

    /** A sentence under the toolbar, written in place (no repaint - the focus stays). */
    private Say = (Words: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Screen.Note = nz(Words);
            const El: HTMLElement | null = document.getElementById(AppMoneyView.NoteId);
            if (El !== null) { El.textContent = this.Screen.Note; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.Say");
        }
    };

    /** ⭐ Overdue money for the Today strip on Chats ("" = none): up to 5, oldest first, each a link to its message. */
    public OverdueHtml = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Late: AppMoneyEntry[] = AppMoneyModel.Overdue(AppMoneyModel.Entries(this.Host.MoneyIndex(), AppMoneyRun.Today()));
            if (Late.length === 0) { return ""; }
            const Out: string[] = ["<section class=\"mtoday\" aria-label=\"Money overdue\"><h2><a href=\"#money\">Money overdue</a></h2><ul>"];
            for (const E of Late.slice(0, 5)) {
                let Way: string = "You owe";
                if (E.Direction === "in") { Way = "Owed to you"; }
                Out.push("<li><a href=\"#c/" + kHTMLCore.EscapeAttr(encodeURIComponent(E.Href)) + "/" + kHTMLCore.EscapeAttr(encodeURIComponent(E.Anchor))
                    + "\">" + kHTMLCore.EscapeHtml(E.Contact + " - " + Way + " " + AppMoneyView.Amount(E.Amount, E.Currency) + ", due " + E.Due) + "</a></li>");
            }
            Out.push("</ul></section>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.OverdueHtml");
            return "";
        }
    };

    /** Open, unsettled balance with one contact, as header words ("" = none): "Owes you 120.00 EUR". */
    public BalanceWords = (Href: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Words: string[] = [];
            const All: AppMoneyEntry[] = AppMoneyModel.Entries(this.Host.MoneyIndex(), AppMoneyRun.Today());
            for (const T of AppMoneyModel.BalanceFor(All, Href)) {
                if (T.In > T.Out) { Words.push("Owes you " + AppMoneyView.Amount(T.In - T.Out, T.Currency)); }
                else { Words.push("You owe " + AppMoneyView.Amount(T.Out - T.In, T.Currency)); }
            }
            return Words.join(", ");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyRun.BalanceWords");
            return "";
        }
    };
}

/** One `/money` request, answered once into `AppMoneyRun.Answered`. */
export class AppMoneyCall {
    private Run: AppMoneyRun;
    private Entry: AppMoneyEntry;
    private Task: string;

    public constructor(Run: AppMoneyRun, Entry: AppMoneyEntry, Task: string) {
        this.Run = Run;
        this.Entry = Entry;
        this.Task = Task;
    }

    public Go = (Body: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Run.HostOf().MoneyPost(Body, this.OnAnswer);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyCall.Go");
        }
    };

    private OnAnswer = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Run.Answered(this.Entry, this.Task, Status, Text);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMoneyCall.OnAnswer");
        }
    };
}
